import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { LocalService } from './local.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
	selector: 'nc-poi',
	templateUrl: './poi.component.html',
	styleUrls: ['./poi.component.less']
})
export class PoiComponent {
	@Output() ncClick = new EventEmitter();
	@Output() ncClear = new EventEmitter();
	@Input() ncMapType!: 'baidu' | 'gaode' | 'tianditu' | 'tencent';
	@Input() ncToken: string = '';
	@Input() ncCity: string = '';
	//百度：https://mapopen-website-wiki.cdn.bcebos.com/static_zip/BaiduMap_cityCode_1102.zip
	//高德：https://a.amap.com/lbs/static/code_resource/AMap_adcode_citycode.zip
	//天地图：https://download.tianditu.gov.cn/download/xzqh/AdminCode.csv
	//腾讯：https://lbs.qq.com/service/webService/webServiceGuide/search/webServiceDistrict#6
	@Input() ncPlaceholder: string = '搜地点';
	@Input() ncWidth: string = '300px';
	@Input() ncShowType: 'simple' | 'detailed' = 'simple';
	@Input() ncDelay: number = 500;

	parametersOk: boolean = true;//参数是否输入完整，不完整不显示组件dom
	searchBarShow: boolean = false;//poi搜索条显示
	poiKey: string = '';//poi搜索关键字
	poiInputing: boolean = false;
	poiTipsList: Array<any> = [];//poi搜索提示结果
	poiTipsListShow: boolean = false;//是否显示poi提示列表
	$poiKeyChangeSubject = new Subject<any>();

	constructor(private eleRef: ElementRef, private http: HttpClient, private ls: LocalService) { }

	ngOnInit() {
		if (!this.ncMapType) {
			this.parametersOk = false;
			console.error('ncMapType为空');
		}

		if (this.ncToken == '') {
			this.parametersOk = false;
			console.error('ncToken为空，请根据地图类型传入相应token');
		}

		//监听搜索输入框值变化，500ms没变才执行搜索
		this.$poiKeyChangeSubject.pipe(
			debounceTime(this.ncDelay),
			distinctUntilChanged((n: string, o: string): boolean => {
				return n === o;
			})
		).subscribe((_value: any) => {
			if (_value) {
				this.searchPoiTips();
			} else {
				this.clearPoi();
			}
		});
	}


	//win11微软输入法输入中文时会触发mouseleave
	//-----解决思路如下----：
	//1、通过document.activeElement.id判断input有焦点时searchBarLeave不执行searchBarShow=false
	//2、但是input焦点一直存在，searchBarLeave永远不执行searchBarShow=false
	//3、接着增加searchInputLeave，在鼠标离开input时取消input焦点，解决上步
	//4、但是现在输入中文时，输入第一个字母又会触发searchInputLeave，关闭了焦点，执行了搜索（输入zai时变成了z爱）
	//5、接着增加poiInputing，keydown时poiInputing=true，第一个字母输入时不执行searchInputLeave里的取消焦点，解决上步，
	//6、但是poiInputing就一直为true了，searchInputLeave永远不执行取消input焦点了
	//7、接着增加on-keyup时poiInputing=false，解决上步
	searchBarLeave() {
		setTimeout(() => {
			if (document.activeElement && document.activeElement.id != 'searchInput') {
				this.searchBarShow = false
			}
		})
	}
	searchInputLeave() {
		if (this.poiInputing) {
			return;
		}
		setTimeout(() => {
			let searchInput = this.eleRef.nativeElement.querySelector('#searchInput');
			if (searchInput) {
				searchInput.blur();
			}
		})
	}
	showSearch() {
		this.searchBarShow = true;
		setTimeout(() => {
			let searchInput = this.eleRef.nativeElement.querySelector('#searchInput');
			if (searchInput) {
				searchInput.focus();
			}
		})
	}

	//输入框值变化
	poiKeyChange() {
		this.$poiKeyChangeSubject.next(this.poiKey);
	}

	//poi搜索提示
	async searchPoiTips() {
		if (this.ncMapType == 'gaode') {
			let url = `https://restapi.amap.com/v3/assistant/inputtips?key=${this.ncToken}&keywords=${this.poiKey}&city=${this.ncCity}`;
			this.http.get(url).subscribe((res: any) => {
				this.poiTipsListShow = true;
				if (res['status'] == 1) {
					this.poiTipsList = res['tips'];
					for (let l of this.poiTipsList) {
						if (l.location.length > 0) {
							let GCJ02 = l.location.split(',');
							l['locationWGS84'] = this.ls.gcj02towgs84(Number(GCJ02[0]), Number(GCJ02[1]));
						} else {
							l['locationWGS84'] = [];
						}
					}
				} else {
					this.poiTipsList = [];
				}
			})
		} else if (this.ncMapType == 'baidu') {
			let url = '';
			if (this.ncCity == '') {
				url = `https://api.map.baidu.com/place/v2/suggestion?query=${this.poiKey}&region=全国&output=json&ak=${this.ncToken}`;
			} else {
				url = `https://api.map.baidu.com/place/v2/suggestion?query=${this.poiKey}&region=${this.ncCity}&output=json&ak=${this.ncToken}`;
			}
			this.ls.jsonpFunction({
				url: url,
				success: (res: any) => {
					this.poiTipsListShow = true;
					if (res['status'] == 0) {
						this.poiTipsList = res['result'];
						for (let l of this.poiTipsList) {
							if (l.location.lat) {
								let GCJ02 = this.ls.bd09togcj02(Number(l.location.lng), Number(l.location.lat));
								l['locationWGS84'] = this.ls.gcj02towgs84(Number(GCJ02[0]), Number(GCJ02[1]));
							} else {
								l['locationWGS84'] = [];
							}
						}
					} else {
						this.poiTipsList = [];
					}
				}
			})
		} else if (this.ncMapType == 'tianditu') {
			let url = '';
			if (this.ncCity == '') {
				url = `http://api.tianditu.gov.cn/v2/search?postStr={"keyWord":"${this.poiKey}","level": 18,"mapBound": "-180,-90,180,90","queryType":7,"start":0,"count":10,"show":2}&type=query&tk=${this.ncToken}`;
			} else {
				url = `http://api.tianditu.gov.cn/v2/search?postStr={"keyWord":"${this.poiKey}","specify": ${this.ncCity},"level": 18,"mapBound": "-180,-90,180,90","queryType":7,"start":0,"count":10,"show":2}&type=query&tk=${this.ncToken}`;
			}
			this.http.get(url).subscribe((res: any) => {
				this.poiTipsListShow = true;
				if (res['status'].infocode == 1000) {
					this.poiTipsList = res['pois'] || [];
					for (let l of this.poiTipsList) {
						if (l.lonlat.length > 0) {
							let temp = l.lonlat.split(',');
							l['locationWGS84'] = [Number(temp[0]), Number(temp[1])]
						} else {
							l['locationWGS84'] = [];
						}
					}
				} else {
					this.poiTipsList = [];
				}
			})
		} else if (this.ncMapType == 'tencent') {
			let url = `https://apis.map.qq.com/ws/place/v1/suggestion?region=${this.ncCity}&keyword=${this.poiKey}&key=${this.ncToken}&output=jsonp`;
			this.ls.jsonpFunction({
				url: url,
				success: (res: any) => {
					this.poiTipsListShow = true;
					if (res['status'] == 0) {
						this.poiTipsList = res['data'];
						for (let l of this.poiTipsList) {
							if (l.location.lat) {
								l['locationWGS84'] = this.ls.gcj02towgs84(Number(l.location.lng), Number(l.location.lat));
							} else {
								l['locationWGS84'] = [];
							}
						}
					} else {
						this.poiTipsList = [];
					}
				}
			})
		}
	}

	//poi提示点击
	//若提示列表经纬度则直接使用
	//若没有则使用提示名字调用详细接口，取返回结果第一条的经纬度
	poiTipsClick(item: any) {
		this.searchBarShow = false;
		if (item.locationWGS84.length > 0) {
			this.ncClick.emit(item)
		} else {
			if (this.ncMapType == 'gaode') {
				let url = `https://restapi.amap.com/v3/place/text?key=${this.ncToken}&keywords=${item.name}&city=${this.ncCity}`;
				this.http.get(url).subscribe((res: any) => {
					if (res['status'] == 1) {
						if (res['pois'][0].location.length > 0) {
							let GCJ02 = res['pois'][0].location.split(',');
							item['locationWGS84'] = this.ls.gcj02towgs84(Number(GCJ02[0]), Number(GCJ02[1]));
							this.ncClick.emit(item);
						} else {
							this.ncClick.emit(item);
						}
					} else {
						this.ncClick.emit(item);
					}
				})
			} else if (this.ncMapType == 'baidu') {
				let url = '';
				if (this.ncCity == '') {
					url = `https://api.map.baidu.com/place/v2/search?query=${item.name}&region=全国&output=json&ak=${this.ncToken}`
				} else {
					url = `https://api.map.baidu.com/place/v2/search?query=${item.name}&region=${this.ncCity}&output=json&ak=${this.ncToken}`
				}
				this.ls.jsonpFunction({
					url: url,
					success: (res: any) => {
						if (res['status'] == 0) {
							if (res['results'][0].location.lat) {
								let GCJ02 = this.ls.bd09togcj02(Number(res['results'][0].location.lng), Number(res['results'][0].location.lat));
								item['locationWGS84'] = this.ls.gcj02towgs84(Number(GCJ02[0]), Number(GCJ02[1]));
								this.ncClick.emit(item);
							} else {
								this.ncClick.emit(item);
							}
						} else {
							this.ncClick.emit(item);
						}
					}
				})
			} else if (this.ncMapType == 'tianditu') {
				this.ncClick.emit(item);
			} else if (this.ncMapType == 'tencent') {
				let boundary = '';
				if (this.ncCity == '') {
					boundary = 'region(全国, 1)'
				} else {
					boundary = `region(${this.ncCity}, 1)`
				}
				let url = `https://apis.map.qq.com/ws/place/v1/search?boundary=${boundary}&keyword=${item.title}&key=${this.ncToken}&output=jsonp`;
				this.ls.jsonpFunction({
					url: url,
					success: (res: any) => {
						if (res['status'] == 0) {
							if (res['data'][0].location.lat) {
								item['locationWGS84'] = this.ls.gcj02towgs84(Number(res['data'][0].location.lng), Number(res['data'][0].location.lat));
								this.ncClick.emit(item);
							} else {
								this.ncClick.emit(item);
							}
						} else {
							this.ncClick.emit(item);
						}
					}
				})
			}
		}
	}

	//清除poi
	clearPoi() {
		this.poiKey = '';
		this.poiTipsList = [];
		this.poiTipsListShow = false;
		this.ncClear.emit();
	}
}